import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers';
import _ from 'lodash';

/**
 * @typedef {Object} FormProps
 * @property {React.ReactNode} children
 * @property {Function} onSubmit - Custom submit handler that receives form values
 * @property {Object} [defaultValues]
 * @property {Object} [validationSchema]
 * @property {boolean} [watch]
 * @property {string} [mode]
 * @property {Object} [prefilledValues]
 * @property {Function} [onError] - Error handler that receives validation errors
 * @property {boolean} [shouldUnregister]
 *
 * @param {FormProps & Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>} props
 */
const Form = ({
  children,
  onSubmit,
  defaultValues = {},
  validationSchema,
  watch = false,
  mode = `onSubmit`,
  prefilledValues = {},
  onError = null,
  shouldUnregister = false,
  ...props
}) => {
  const formArgs = {
    mode,
    defaultValues,
    shouldUnregister,
  };

  if (validationSchema) {
    formArgs.resolver = yupResolver(validationSchema);
  }

  const methods = useForm(formArgs);

  useEffect(() => {
    if (!_.isEmpty(prefilledValues)) {
      _.forEach(Object.entries(prefilledValues), ([name, value]) => {
        if (value) {
          methods.setValue(name, value, { shouldDirty: true, shouldValidate: true });
        }
      });
    }
    // methods.setValue is stable and doesn't need to be in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledValues]);

  if (watch) {
    methods.watch();
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onError)} onReset={() => methods.reset(defaultValues)} {...props}>
        {children}
      </form>
    </FormProvider>
  );
};

export default Form;
